import { startCase } from 'lodash';

import { CHANNEL, isSocialChannel } from 'src/data/channel';
import { getIsEmailChannel } from 'src/data/performance';

export const KNOWN_METRIC = {
  clicks: 'clicks',
  opens: 'opens',
  reach: 'reach',
  ctr: 'ctr',
};

export const PERFORMANCE_GOAL = {
  ctr: 'ctr',
  conversionRate: 'conversion_rate',
  totalConversions: 'total_conversions',
  reach: 'reach',
  engagementRate: 'engagement_rate',
  openRate: 'open_rate',
  impressions: 'impressions',
};

const PERFORMANCE_GOAL_PROPS = {
  [PERFORMANCE_GOAL.ctr]: {
    label: 'CTR',

    // CLICKS / IMPRESSIONS * 100
    calculate: ({ impressions, calculateMetric }) => {
      // In some cases CTR is already calculated by BE
      const ctr = calculateMetric([KNOWN_METRIC.ctr]) || 0;
      if (ctr > 0) {
        return ctr;
      }

      const clicks = calculateMetric([KNOWN_METRIC.clicks]) || 0;
      return impressions > 0 ? (clicks / impressions) * 100 : 0;
    },
  },

  [PERFORMANCE_GOAL.conversionRate]: {
    label: 'Conversion Rate',

    // METRIC / CLICKS * 100
    calculate: ({ calculateMetric, metricType }) => {
      const clicks = calculateMetric([KNOWN_METRIC.clicks]) || 0;
      const actions = calculateMetric([metricType]) || 0;
      return clicks > 0 ? (actions / clicks) * 100 : 0;
    },
  },

  [PERFORMANCE_GOAL.totalConversions]: {
    label: 'Total Conversions',

    // METRIC
    calculate: ({ calculateMetric, metricType }) => {
      return calculateMetric([metricType]) || 0;
    },
  },

  [PERFORMANCE_GOAL.reach]: {
    label: 'Reach',

    // REACH || IMPRESSIONS
    calculate: ({ calculateMetric, impressions }) => {
      const reach = calculateMetric([KNOWN_METRIC.reach]);
      return reach || impressions || 0;
    },
  },

  [PERFORMANCE_GOAL.engagementRate]: {
    label: 'Engagement Rate',

    // ALL_METRICS / (REACH || IMPRESSIONS) * 100
    calculate: ({ impressions, calculateMetric }) => {
      const allActions = calculateMetric() || 0;
      const reach = calculateMetric([KNOWN_METRIC.reach]);
      if (reach > 0) {
        return ((allActions - reach) / reach) * 100;
      }

      return impressions > 0 ? (allActions / impressions) * 100 : 0;
    },
  },

  [PERFORMANCE_GOAL.openRate]: {
    label: 'Open Rate',

    // OPENS / IMPRESSIONS * 100
    calculate: ({ impressions, calculateMetric }) => {
      const opens = calculateMetric([KNOWN_METRIC.opens]) || 0;
      return impressions > 0 ? (opens / impressions) * 100 : 0;
    },
  },

  [PERFORMANCE_GOAL.impressions]: {
    label: 'Impressions',

    // IMPRESSIONS
    calculate: ({ impressions }) => {
      return impressions || 0;
    },
  },
};

export const getPerformanceGoalLabel = (goal) => PERFORMANCE_GOAL_PROPS[goal]?.label || '';
export const getPerformanceGoalCalculate = (goal) => PERFORMANCE_GOAL_PROPS[goal]?.calculate;

export const getChannelGoalLabel = ({ channel, goal }) => {
  // "Impressions" goal is a special case, and might have different labels for different channels
  if (goal === PERFORMANCE_GOAL.impressions) {
    if (isSocialChannel(channel)) {
      return 'Reach';
    }
    if (getIsEmailChannel(channel)) {
      return 'Delivered';
    }
  }

  return getPerformanceGoalLabel(goal);
};

export const getPerformanceGoals = (channel) => {
  if (channel === CHANNEL.google) {
    return [PERFORMANCE_GOAL.impressions];
  }

  if (channel === CHANNEL.facebook) {
    return [
      PERFORMANCE_GOAL.ctr,
      PERFORMANCE_GOAL.impressions,
      PERFORMANCE_GOAL.conversionRate,
      PERFORMANCE_GOAL.totalConversions,
    ];
  }

  if (getIsEmailChannel(channel)) {
    return [PERFORMANCE_GOAL.ctr, PERFORMANCE_GOAL.impressions, PERFORMANCE_GOAL.openRate];
  }

  if (isSocialChannel(channel)) {
    // "FB pages" does not support "engagement rate" for now
    if (channel === CHANNEL.facebookPages) {
      return [PERFORMANCE_GOAL.ctr, PERFORMANCE_GOAL.reach];
    }
    return [PERFORMANCE_GOAL.ctr, PERFORMANCE_GOAL.reach, PERFORMANCE_GOAL.engagementRate];
  }

  return [PERFORMANCE_GOAL.ctr, PERFORMANCE_GOAL.impressions];
};

export const goalSupportsConversionType = (goal) =>
  [PERFORMANCE_GOAL.conversionRate, PERFORMANCE_GOAL.totalConversions].includes(goal);

export const goalIsPercentageBased = (goal) =>
  ![
    PERFORMANCE_GOAL.totalConversions,
    PERFORMANCE_GOAL.reach,
    PERFORMANCE_GOAL.impressions,
  ].includes(goal);

export const determineMetricTypes = ({ goal, metricType }) => {
  if (goalSupportsConversionType(goal)) {
    return [metricType];
  }

  if (goal === PERFORMANCE_GOAL.openRate) {
    return [KNOWN_METRIC.opens];
  }

  // fallback to "clicks" and "ctr", as the basic metrics
  return [KNOWN_METRIC.clicks, KNOWN_METRIC.ctr];
};

export const getChartGoalLabel = ({ channel, goal, metricType }) => {
  if (goalSupportsConversionType(goal) && metricType) {
    return startCase(metricType);
  }

  return getChannelGoalLabel({ channel, goal });
};
